/**
 * AgentTypeSelector Component
 * 
 * Phase 1: Type-First Agent Creation
 * 
 * This component displays selectable cards for agent type selection.
 * Agent type must be selected BEFORE any configuration is shown.
 * Once selected, capability_profile is set and cannot be changed.
 */
import React from 'react';
import { motion } from 'motion/react';
import {
  ArrowRight,
  ChatText,
  CheckCircle,
  Lightning,
  Microphone,
  Phone,
  SpeakerHigh,
  WebhooksLogo
} from '@phosphor-icons/react';

/**
 * CANONICAL AGENT MODEL v1.0 - EXACTLY 4 PROFILES
 * 
 * WIZARD TRIGGER LOGIC:
 *   wizard_required = 
 *     capability_profile === "live_voice_s2s_agent"
 *     OR (capability_profile === "voice_input_agent" AND requires_live_meeting_integration === true)
 */
const AGENT_TYPES = [
  {
    id: 'text_agent',
    name: 'Text Agent',
    description: 'Process text, files, and audio files for analysis and automation',
    icon: ChatText,
    color: 'from-blue-500 to-cyan-500',
    capability_profile: 'text_agent',
    features: ['Chat conversations', 'Document processing', 'Audio file analysis', 'Data analysis'],
    requires_wizard: false,  // NEVER - even with audio file uploads
    automationBadge: true,  // Shows "⚡ Automation-ready" badge
    automationTooltip: 'Supports webhooks, triggers, and integrations'
  },
  {
    id: 'live_voice_s2s_agent',
    name: 'Voice Agent – Live (S2S)',
    description: 'Real-time phone conversations with Twilio or Telnyx',
    icon: Phone,
    color: 'from-purple-500 to-pink-500',
    capability_profile: 'live_voice_s2s_agent',
    features: ['Real-time calls', 'Twilio/Telnyx telephony', 'Calendar booking', 'SMS notifications'],
    requires_wizard: true,  // ALWAYS
    badge: 'Setup Wizard'
  },
  {
    id: 'voice_input_agent',
    name: 'Voice Agent – Input Only',
    description: 'Live microphone or meeting platform speech input',
    icon: Microphone,
    color: 'from-green-500 to-emerald-500',
    capability_profile: 'voice_input_agent',
    features: ['Live microphone input', 'Meeting transcription', 'Voice commands', 'Speech-to-text'],
    requires_wizard: false  // Only if requires_live_meeting_integration
  },
  {
    id: 'voice_output_agent',
    name: 'Voice Agent – Output (TTS)',
    description: 'Text-to-speech responses and audio generation',
    icon: SpeakerHigh,
    color: 'from-orange-500 to-amber-500',
    capability_profile: 'voice_output_agent',
    features: ['Text-to-speech', 'Audio responses', 'Voice notifications', 'Accessibility'],
    requires_wizard: false  // NEVER
  }
];

const AgentTypeSelector = ({ onSelect, selectedType, disabled = false }) => {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">What type of agent do you want to create?</h2>
        <p className="text-gray-400">Select the agent type that best fits your use case. This cannot be changed later.</p>
      </div>

      {/* Agent Type Cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {AGENT_TYPES.map((type, index) => {
          const Icon = type.icon;
          const isSelected = selectedType === type.id;
          
          return (
            <motion.button
              key={type.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              onClick={() => !disabled && onSelect(type)}
              disabled={disabled}
              className={`
                relative p-6 rounded-2xl text-left transition-all
                ${isSelected 
                  ? 'bg-gradient-to-br ' + type.color + ' ring-2 ring-white/50' 
                  : 'bg-[rgba(255,255,255,0.03)] hover:bg-[rgba(255,255,255,0.06)] border border-[rgba(255,255,255,0.1)] hover:border-[rgba(255,255,255,0.2)]'
                }
                ${disabled && !isSelected ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              {/* Selected Checkmark */}
              {isSelected && (
                <div className="absolute top-4 right-4">
                  <CheckCircle size={24} className="text-white" />
                </div>
              )}

              {/* Icon */}
              <div className={`
                w-12 h-12 rounded-xl flex items-center justify-center mb-4
                ${isSelected ? 'bg-white/20' : 'bg-gradient-to-br ' + type.color}
              `}>
                <Icon size={24} className="text-white" />
              </div>

              {/* Title & Description */}
              <h3 className="text-lg font-semibold text-white mb-2">{type.name}</h3>
              <p className={`text-sm mb-4 ${isSelected ? 'text-white/80' : 'text-gray-400'}`}>
                {type.description}
              </p>

              {/* Features */}
              <div className="space-y-1">
                {type.features.map((feature, i) => (
                  <div key={i} className={`flex items-center gap-2 text-xs ${isSelected ? 'text-white/70' : 'text-gray-500'}`}>
                    <Lightning size={12} />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>

              {/* Badge */}
              {type.badge && (
                <div className={`
                  mt-4 inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium
                  ${isSelected ? 'bg-white/20 text-white' : 'bg-purple-500/20 text-purple-400 border border-purple-500/30'}
                `}>
                  <Lightning size={12} />
                  {type.badge}
                </div>
              )}

              {/* Automation Badge - prominent badge for text_agent */}
              {type.automationBadge && (
                <div 
                  className={`
                    mt-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium
                    ${isSelected 
                      ? 'bg-white/20 text-white' 
                      : 'bg-amber-500/15 text-amber-400 border border-amber-500/30'
                    }
                  `}
                  title={type.automationTooltip}
                >
                  <Lightning size={12} className="text-amber-400" />
                  <span>Automation-ready</span>
                </div>
              )}
            </motion.button>
          );
        })}
      </div>

      {/* Continue Button */}
      {selectedType && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex justify-center mt-8"
        >
          <div className="text-center">
            <p className="text-sm text-gray-400 mb-3">
              You selected: <span className="text-white font-semibold">{AGENT_TYPES.find(t => t.id === selectedType)?.name}</span>
            </p>
            <p className="text-xs text-amber-400 mb-4">
              ⚠️ Agent type cannot be changed after creation
            </p>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export { AGENT_TYPES };
export default AgentTypeSelector;
